/**
 * 기능(블록/확장) 스펙 레지스트리 — 엔진(core/serialize)은 계층 유지, 블록/확장은 수직 슬라이스.
 *
 * 각 기능은 features/<name>/index.js 에서 스펙 객체를 default export 한다:
 *   {
 *     name: string,
 *     blocks?: { [type]: { toElement(block)->HTMLElement, fromElement(el)->block } },  // 커스텀 블록 직렬화
 *     slash?: (ctx) => SlashItem[],   // 슬래시 메뉴 기여(ctx: 에디터 컨텍스트)
 *     // (확장 예정) paste?, controls?(editorEl, ctx), inlineStyles?, api?
 *   }
 *
 * buildRegistry 가 스펙들을 직렬화 lookup(toEl/fromEl)으로 합치고,
 * gatherSlash(ctx) 로 슬래시 항목을 수집한다.
 * 직렬화 lookup 은 serialize/document.js(합성 루트)가, 슬래시는 editor.js(오케스트레이터)가 소비.
 *
 * 공존 전략: 미이전 기능은 기존 배선 유지 → 레지스트리 결과와 병합(동작 동일). 한 기능씩 이전.
 */

export function buildRegistry(features) {
  const list = Array.isArray(features) ? features.filter(Boolean) : [];
  const toEl = {};
  const fromEl = {};
  for (const f of list) {
    if (!f || !f.blocks) continue;
    for (const type of Object.keys(f.blocks)) {
      const spec = f.blocks[type];
      if (spec && typeof spec.toElement === "function") toEl[type] = spec.toElement;
      if (spec && typeof spec.fromElement === "function") fromEl[type] = spec.fromElement;
    }
  }
  return {
    features: list,
    toEl,
    fromEl,
    /** 모든 기능의 슬래시 항목 수집(ctx: 에디터가 주입하는 컨텍스트) */
    gatherSlash(ctx) {
      const out = [];
      for (const f of list) {
        if (f && typeof f.slash === "function") {
          const items = f.slash(ctx);
          if (Array.isArray(items)) out.push(...items);
        }
      }
      return out;
    },
    /** 모든 기능의 인터랙션 컨트롤러 부착. controls(editorEl, ctx)는 { destroy, refresh? } 핸들 반환 → 핸들 배열 반환 */
    attachControls(editorEl, ctx) {
      const handles = [];
      for (const f of list) {
        if (f && typeof f.controls === "function") {
          const h = f.controls(editorEl, ctx);
          if (h) handles.push(h);
        }
      }
      return handles;
    },
    /** 모든 기능의 붙여넣기 규칙 수집. paste(ctx) => [{ match(text,html), handle(text,html)->bool }] */
    gatherPaste(ctx) {
      const out = [];
      for (const f of list) {
        if (f && typeof f.paste === "function") {
          const rules = f.paste(ctx);
          if (Array.isArray(rules)) out.push(...rules);
        }
      }
      return out;
    },
  };
}
